import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { T, scoreColor } from "../../tokens";
import { useAuthStore } from "../../store";

// ── Types ─────────────────────────────────────────────────────────────────

type JobDetailData = {
  job: {
    id: string; title: string; company: string; source: string;
    location: string | null; isActive: boolean; portalTrust: string | null;
    salaryRaw: string | null; salaryMinLpa: number | null; salaryMaxLpa: number | null;
    workAuthTag: string | null; url: string;
    postedAt: string | null; firstSeenAt: string; lastSeenAt: string;
    repostCount: number; descPreview: string | null; descLen: number; category: string;
  };
  matchStats: {
    total: number;
    scoreDistribution: Record<string, number>;
  };
  topMatches: {
    userId: string; userName: string; userEmail: string;
    weightedScore: number | null; semanticScore: number | null;
    modelUsed: string | null; autoApplyEligible: boolean;
    isStale: boolean; scoredAt: string;
  }[];
};

// ── Helpers ───────────────────────────────────────────────────────────────

function fmtDate(d: string | null) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" });
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start",
      padding: "6px 0", borderBottom: `1px solid ${T.b0}`, gap: "12px" }}>
      <span style={{ fontFamily: T.body, fontSize: "11px", color: T.t3, flexShrink: 0 }}>{label}</span>
      <span style={{ fontFamily: T.mono, fontSize: "11px", color: T.t1, textAlign: "right" }}>{value}</span>
    </div>
  );
}

const BUCKET_ORDER = ["excellent", "good", "fair", "poor"];
const BUCKET_COLOR: Record<string, string> = {
  excellent: T.ok, good: T.warn, fair: T.mid, poor: T.err,
};
const TRUST_COLOR: Record<string, string> = { high: T.ok, medium: T.warn, low: T.err };

// ── Panel ─────────────────────────────────────────────────────────────────

export function JobDetail({ jobId, onClose }: { jobId: string; onClose: () => void }) {
  const token = useAuthStore((s) => s.token);

  const q = useQuery<JobDetailData>({
    queryKey: ["admin-job-detail", jobId],
    queryFn: async () => { const { data } = await axios.get(`/api/admin/jobs/${jobId}`); return data; },
    enabled: !!token, retry: false,
  });

  return (
    <>
      {/* Backdrop */}
      <div onClick={onClose}
        style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 40 }} />

      {/* Panel */}
      <div style={{ position: "fixed", top: 0, right: 0, bottom: 0,
        width: "min(520px, 100vw)", background: T.s1, borderLeft: `1px solid ${T.b1}`,
        zIndex: 50, overflowY: "auto", display: "flex", flexDirection: "column" }}>

        {/* Header */}
        <div style={{ padding: "18px 20px 14px", borderBottom: `1px solid ${T.b0}`, flexShrink: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div style={{ flex: 1, overflow: "hidden", paddingRight: "8px" }}>
              {q.isLoading && <div style={{ fontFamily: T.body, fontSize: "13px", color: T.t3 }}>Loading…</div>}
              {q.data && (
                <>
                  <div style={{ fontFamily: T.display, fontSize: "16px", fontWeight: 700,
                    color: T.t0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {q.data.job.title}
                  </div>
                  <div style={{ fontFamily: T.mono, fontSize: "11px", color: T.t2, marginTop: "2px" }}>
                    {q.data.job.company}
                    {q.data.job.location ? ` · ${q.data.job.location}` : ""}
                  </div>
                  <div style={{ display: "flex", gap: "6px", marginTop: "6px", flexWrap: "wrap" }}>
                    <span style={{ fontFamily: T.mono, fontSize: "9px", color: T.tealSoft,
                      padding: "2px 6px", background: T.tealBg, border: `1px solid ${T.tealBdr}`,
                      borderRadius: "4px" }}>{q.data.job.category}</span>
                    {q.data.job.portalTrust && (
                      <span style={{ fontFamily: T.mono, fontSize: "9px",
                        color: TRUST_COLOR[q.data.job.portalTrust] ?? T.t3,
                        padding: "2px 6px", background: T.s3, border: `1px solid ${T.b1}`,
                        borderRadius: "4px" }}>trust:{q.data.job.portalTrust}</span>
                    )}
                    {!q.data.job.isActive && (
                      <span style={{ fontFamily: T.mono, fontSize: "9px", color: T.err,
                        padding: "2px 6px", background: T.errBg, border: `1px solid ${T.errBdr}`,
                        borderRadius: "4px" }}>inactive</span>
                    )}
                  </div>
                </>
              )}
            </div>
            <button type="button" onClick={onClose}
              style={{ background: "none", border: "none", color: T.t2, fontSize: "20px",
                cursor: "pointer", lineHeight: 1, padding: "0 4px", flexShrink: 0 }}
              aria-label="Close">×</button>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: "16px 20px", flex: 1 }}>
          {q.isError && (
            <p style={{ fontFamily: T.body, fontSize: "13px", color: T.err }}>Failed to load job details.</p>
          )}

          {q.data && (() => {
            const { job, matchStats, topMatches } = q.data;
            return (
              <>
                {/* Metadata */}
                <div style={{ background: T.s2, border: `1px solid ${T.b0}`,
                  borderRadius: "8px", padding: "12px 16px", marginBottom: "12px" }}>
                  <div style={{ fontFamily: T.mono, fontSize: "9px", color: T.t3,
                    letterSpacing: "0.10em", textTransform: "uppercase", marginBottom: "8px" }}>Job Info</div>
                  <Row label="Source"      value={job.source} />
                  <Row label="Salary"      value={
                    job.salaryRaw ?? (job.salaryMinLpa || job.salaryMaxLpa
                      ? `${job.salaryMinLpa ?? "?"}–${job.salaryMaxLpa ?? "?"} LPA` : "—")
                  } />
                  <Row label="Work auth"   value={job.workAuthTag ?? "—"} />
                  <Row label="Repost cnt"  value={job.repostCount} />
                  <Row label="JD length"   value={
                    <span style={{ color: job.descLen >= 250 ? T.ok : job.descLen > 0 ? T.warn : T.err }}>
                      {job.descLen} chars
                    </span>
                  } />
                  <Row label="Posted"      value={fmtDate(job.postedAt)} />
                  <Row label="First seen"  value={fmtDate(job.firstSeenAt)} />
                  <Row label="Last seen"   value={fmtDate(job.lastSeenAt)} />
                  <div style={{ paddingTop: "8px" }}>
                    <a href={job.url} target="_blank" rel="noopener noreferrer"
                      style={{ fontFamily: T.mono, fontSize: "10px", color: T.tealSoft }}>
                      Open job URL ↗
                    </a>
                  </div>
                </div>

                {/* JD preview */}
                {job.descPreview && (
                  <div style={{ background: T.s2, border: `1px solid ${T.b0}`,
                    borderRadius: "8px", padding: "12px 16px", marginBottom: "12px" }}>
                    <div style={{ fontFamily: T.mono, fontSize: "9px", color: T.t3,
                      letterSpacing: "0.10em", textTransform: "uppercase", marginBottom: "8px" }}>
                      JD Preview {job.descLen > 800 ? "(first 800 chars)" : ""}
                    </div>
                    <p style={{ fontFamily: T.body, fontSize: "11px", color: T.t2,
                      margin: 0, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
                      {job.descPreview}
                    </p>
                  </div>
                )}

                {/* Match stats */}
                <div style={{ background: T.s2, border: `1px solid ${T.b0}`,
                  borderRadius: "8px", padding: "12px 16px", marginBottom: "12px" }}>
                  <div style={{ fontFamily: T.mono, fontSize: "9px", color: T.t3,
                    letterSpacing: "0.10em", textTransform: "uppercase", marginBottom: "8px" }}>
                    Match Stats · {matchStats.total} total
                  </div>
                  {BUCKET_ORDER.map((bucket) => {
                    const n = matchStats.scoreDistribution[bucket] ?? 0;
                    const pct = matchStats.total > 0 ? Math.round((n / matchStats.total) * 100) : 0;
                    return (
                      <div key={bucket} style={{ marginBottom: "8px" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "3px" }}>
                          <span style={{ fontFamily: T.mono, fontSize: "10px",
                            color: BUCKET_COLOR[bucket], textTransform: "capitalize" }}>{bucket}</span>
                          <span style={{ fontFamily: T.mono, fontSize: "10px", color: T.t1 }}>{n} · {pct}%</span>
                        </div>
                        <div style={{ height: "3px", background: T.b1, borderRadius: "2px" }}>
                          <div style={{ height: "100%", width: `${pct}%`,
                            background: BUCKET_COLOR[bucket], borderRadius: "2px" }} />
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Top matched users */}
                {topMatches.length > 0 && (
                  <div style={{ background: T.s2, border: `1px solid ${T.b0}`,
                    borderRadius: "8px", padding: "12px 16px" }}>
                    <div style={{ fontFamily: T.mono, fontSize: "9px", color: T.t3,
                      letterSpacing: "0.10em", textTransform: "uppercase", marginBottom: "8px" }}>
                      Top Matched Users
                    </div>
                    {topMatches.map((m) => {
                      const score = Math.round(m.weightedScore ?? 0);
                      return (
                        <div key={m.userId} style={{ display: "flex", justifyContent: "space-between",
                          alignItems: "center", padding: "6px 0",
                          borderBottom: `1px solid ${T.b0}` }}>
                          <div>
                            <div style={{ fontFamily: T.body, fontSize: "12px", color: T.t0 }}>{m.userName}</div>
                            <div style={{ fontFamily: T.mono, fontSize: "10px", color: T.t2 }}>{m.userEmail}</div>
                            <div style={{ fontFamily: T.mono, fontSize: "9px", color: T.t3, marginTop: "2px" }}>
                              {fmtDate(m.scoredAt)}{m.modelUsed ? ` · ${m.modelUsed}` : ""}
                              {m.isStale ? " · stale" : ""}
                              {m.autoApplyEligible ? " · auto✓" : ""}
                            </div>
                          </div>
                          <span style={{ fontFamily: T.mono, fontSize: "14px", fontWeight: 700,
                            color: scoreColor(score), fontVariantNumeric: "tabular-nums" }}>
                            {score}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </div>
    </>
  );
}
